/**
 * A location, as the Locations page edits it: a `Location` that AKB wrote,
 * the ones with a `FHIR_IDENTIFIER_SYSTEMS.LOCATION` identifier. This file
 * holds only data description, no runtime logic.
 */
import MapPin from "lucide-react/dist/esm/icons/map-pin";

import type { ConfigSchema } from "@/types/clinic-configuration";
import {
  FHIR_EXTENSIONS,
  FHIR_IDENTIFIER_SYSTEMS,
} from "@/constants/fhir.constants";
import { ROUTES } from "@/constants/routes.constants";

/**
 * Needs for i18next-scanner
 */
// t('Locations')
// t('Name')
// t('Address')
// t('Phone')
// t('Timezone')
// t('Directions')
// t('How patients find the location once they arrive')
// t('Directions link')
// t('A Waze or Google Maps link')
// t('Opening hours')
// t('Open on {{day}}')
// t('Closed')
// t('Availability notes')
// t('Anything about the hours that the table cannot say, e.g. holidays')
// t('Location ID')
export const locationSchema = {
  fields: {
    name: {
      kind: "text",
      path: "name",
      title: "Name",
      required: true,
      orientation: "horizontal",
      appearance: "standard",
    },
    address: {
      kind: "text",
      path: "address.text",
      title: "Address",
      orientation: "horizontal",
      appearance: "standard",
    },
    phone: {
      kind: "text",
      path: "telecom.value",
      match: { system: "phone" },
      title: "Phone",
      orientation: "horizontal",
      appearance: "standard",
    },
    directions: {
      kind: "text",
      path: "description",
      title: "Directions",
      description: "How patients find the location once they arrive",
      layout: "stacked",
    },
    hours: {
      kind: "weekSchedule",
      path: "hoursOfOperation",
      converter: "hoursOfOperation",
      title: "Opening hours",
      presentation: {
        rangesPerDay: 2,
        dayToggleLabel: "Open on {{day}}",
        offLabel: "Closed",
      },
    },
    locationId: {
      kind: "text",
      path: "identifier.value",
      match: { system: FHIR_IDENTIFIER_SYSTEMS.LOCATION },
      title: "Location ID",
      readonly: true,
    },
  },
  extensions: {
    timezone: {
      kind: "timezone",
      slug: FHIR_EXTENSIONS.TIMEZONE,
      title: "Timezone",
      orientation: "horizontal",
      appearance: "standard",
    },
    directionsLink: {
      kind: "text",
      slug: FHIR_EXTENSIONS.LOCATION_URL,
      valueType: "string",
      title: "Directions link",
      description: "A Waze or Google Maps link",
      appearance: "standard",
    },
    hoursNarrative: {
      kind: "text",
      slug: FHIR_EXTENSIONS.LOCATION_OPERATING_HOURS_NARRATIVE,
      valueType: "string",
      title: "Availability notes",
      description:
        "Anything about the hours that the table cannot say, e.g. holidays",
      layout: "stacked",
    },
  },
} satisfies ConfigSchema.Resource;

export const locationsPage = {
  to: ROUTES.CLINIC_LOCATIONS,
  label: "Locations",
  icon: MapPin,
  count: "locationCount",
} satisfies ConfigSchema.Page;
